import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import { SxProps } from '@mui/system';
import {
  AskInstructionModel,
  AudioType,
  BulletedListType,
  ComprehensionPassage,
  ContentElementType,
  ExcerptType,
  ExternalResourceType,
  FlashCardElementType,
  GeneralAllowedElements,
  HeadingType,
  ImageType,
  InstructionElementType,
  NumberedListType,
  QuestionElementType,
  ResourceCategoryEnum,
  SolutionElementType,
  SubHeadingType,
  TableType,
  TextBoxType,
  TextCenterType,
  TextElementType,
  TextLeftType,
  TextRightType,
  TextType,
  VideoType,
} from '@protos/content_management/content.db_pb';
import React, { useState } from 'react';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { getMediaBasePath } from '../../../commonUtils/images';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import ImageWrapper from '../../elements/ImageWrapper';
import { FilePreviewCard } from '../FilePreviewCard';
import TypographyHtml from '../Question/TypographyHtml';
import { BlankCounter } from '../Question/question-utils';
import {
  SelectedResourceInfo,
  VideoPlayerWrapper,
} from '../VideoPlayerWrapper/VideoPlayerWrapper';
import { ElementHeadingTag } from './ElementHeadingTag';
import { TableElement } from './Table';
import { ImageComments } from './ImageComments';
import { ImagePopup } from './ImagePopup';
import {
  FeatureGateName,
  useFeatureGate,
} from '../../../commonUtils/featureSchoolGate';

export type ElementType =
  | ContentElementType
  | QuestionElementType
  | SolutionElementType
  | GeneralAllowedElements
  | TextElementType
  | FlashCardElementType
  | InstructionElementType;

interface IProps {
  elements: ElementType[];
  isFibText?: boolean;
  handleAnswerChange?: (value: string, index?: number) => void;
  userAttemptedAnswer?: string[];
  disableInput?: boolean;
  selected_resource_info?: SelectedResourceInfo;
  resourceCategory?: ResourceCategoryEnum;
  imageResizeEnabled?: boolean;
  hideImage?: boolean;
  isCBQQuestion?: boolean;
  disableDownload?: boolean;
  allowUserControls?: boolean /** When false, play/pause stay available but seek, skip, and other controls are locked. Defaults to true. */;
  openPdfInNewTab?: boolean;
  /** Plays this url instead of the element's own, for the selected content language. */
  videoUrlOverride?: string;
}

const styles: IStyles = {
  textElements: {
    width: '100%',
  },
  textElementsWrapper: {
    height: '100%',
    gap: {
      xs: pxToRem(15),
      md: pxTovW(20),
    },
    display: 'flex',
    flexDirection: 'column',
  },
  centerText: {
    textAlign: 'center',
    width: '100%',
  },
  leftText: {
    textAlign: 'left',
  },
  rightText: {
    textAlign: 'right',
  },
  subHeading: {},
  textBoxWrapper: {
    gap: {
      xs: pxToRem(15),
      md: pxTovW(20),
    },
    display: 'flex',
    flexDirection: 'column',
    border: '1.5px solid #007CDC',
    padding: {
      md: `${pxTovW(13)} ${pxTovW(32)}`,
      xs: `${pxToRem(10)} ${pxToRem(11)}`,
    },
    backgroundColor: '#FAFDFF',
    textAlign: 'center',
  },
  heading: {},
  textInput: {
    outline: 0,
    borderWidth: '0 0 2px',
    borderStyle: 'dashed',
    borderColor: 'error.main',
    '&:hover': {
      borderStyle: 'solid',
      borderColor: 'error.main',
    },
    '&:focus': {
      borderStyle: 'solid',
      borderColor: 'error.main',
    },
    textAlign: 'center',
    fontSize: {
      xs: pxToRem(16),
      md: pxToRem(21),
    },
  },
  imageWrapper: {
    display: 'inline-block',
    mx: 'auto',
    position: 'relative',
  },
  image: {
    maxHeight: {
      xs: '160px',
      md: '200px',
    },
    maxWidth: '100%',
  },
  imageNormal: {
    maxHeight: {
      xs: '160px',
      md: '600px',
    },
    maxWidth: '100%',
  },
  resizeBox: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    cursor: 'pointer',
    padding: '7px',
    border: '1px solid #007CDC',
    backgroundColor: '#DBF0FF',
    borderRadius: '8px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resize: {
    height: '15px',
    width: '15px',
    display: 'block',
  },
  bulletList: {
    margin: 0,
    gap: {
      xs: pxToRem(15),
      md: pxTovW(20),
    },
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: {
      xs: pxToRem(16),
      md: pxTovW(26),
    },
    'li::marker': {
      // Styling as of element text
      fontFamily: 'Lato',
      color: '#333333',
      fontWeight: 400,
      fontSize: {
        md: pxToRem(24),
        xs: pxToRem(16),
      },
      lineHeight: 1,
    },
  },
  tagWrapper: {
    paddingLeft: {
      xs: pxToRem(8),
      md: pxToRem(8),
    },
    borderLeft: {
      md: '8px solid #F8C807',
      xs: '4px solid #F8C807',
    },
  },
  accordion: {
    backgroundColor: '#F2F2F2',
    '&:before': {
      backgroundColor: '#F2F2F2',
    },
    boxShadow: 'none',
    borderRadius: '5px',
    width: 'max-content',
    minWidth: {
      xs: '100%',
      md: '60%',
    },
    maxWidth: '100%',
    '&.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      minHeight: '48px',
      margin: 0,
      borderBottom: '1px solid #E1E1E1',
    },
    '& .MuiAccordionSummary-root, .MuiAccordionDetails-root': {
      paddingX: {
        xs: pxToRem(13),
        md: pxToRem(18),
      },
    },
  },
  audioBox: {
    backgroundColor: 'common.white',
    borderRadius: '10px',
    maxWidth: {
      md: pxToRem(648),
    },
    padding: {
      xs: pxToRem(10),
      md: pxToRem(14),
    },
    display: 'flex',
    flexDirection: 'row',
    gap: {
      xs: pxToRem(10),
      md: pxToRem(14),
    },
    boxShadow: {
      md: '0px 3px 15px #0000001F',
      xs: '0px 3px 15px #00000029',
    },
  },
  audioImage: {
    width: {
      xs: pxToRem(56),
      md: pxToRem(100),
    },
  },
  audioWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& .rhap_container': {
      boxShadow: 'none',
      padding: 0,
      backgroundColor: 'unset',
    },
    '& .rhap_progress-container': {
      marginRight: 0,
    },
    '& .rhap_controls-section': {
      margin: 0,
      justifyContent: 'flex-end',
      '&>div': {
        color: '#838383',
      },
    },
    '& .rhap_download-progress': {
      backgroundColor: '#DDD9FF',
    },
    '& .rhap_progress-filled': {
      backgroundColor: '#007CDC',
    },
    '& .rhap_progress-indicator': {
      backgroundColor: '#007CDC',
    },
    '& .rhap_main-controls-button': {
      color: '#007CDC',
    },
    flexGrow: 1,
  },
};

const ElementRenderer = (props: IProps) => {
  const {
    elements,
    isFibText,
    handleAnswerChange,
    userAttemptedAnswer,
    disableInput,
    selected_resource_info,
    resourceCategory,
    imageResizeEnabled,
    hideImage,
    isCBQQuestion,
    disableDownload,
    allowUserControls,
    openPdfInNewTab,
    videoUrlOverride,
  } = props;
  const blankCounter = new BlankCounter();
  const [openImageUrl, setOpenImageUrl] = useState<string | null>(null);
  const annotationsEnabled = useFeatureGate(FeatureGateName.annotationsEnabled);

  const textElements = (
    texts: string[],
    sx: SxProps | undefined,
    variant: any,
    component?: any
  ): JSX.Element[] => {
    const adjustedVariant =
      isCBQQuestion && variant === 'elementText' ? 'cbqElementText' : variant;
    return texts.map((text, index) => {
      return (
        <Box component={component} key={index} sx={sx}>
          <TypographyHtml variant={adjustedVariant}>{text}</TypographyHtml>
        </Box>
      );
    });
  };
  const splitText = (t: string): string[] =>
    t.split('<br>').filter((text) => text);

  const renderFibText = (
    qParts: string[],
    style: SxProps | undefined,
    variant: any,
    wrapperComponent?: any
  ) => {
    return (
      <Box component={wrapperComponent}>
        {qParts.map((part, index) => {
          if (index === 0) {
            return (
              part &&
              textElements(
                splitText(part),
                style,
                variant || 'elementText',
                'span'
              )
            );
          }
          const ct = blankCounter ? blankCounter.next() : 0;
          return (
            <>
              <Box
                component={'input'}
                sx={styles.textInput}
                placeholder="TYPE HERE"
                type={'text'}
                value={
                  userAttemptedAnswer ? userAttemptedAnswer[ct] : undefined
                }
                disabled={disableInput}
                onChange={(ev) => {
                  if (handleAnswerChange) {
                    handleAnswerChange(ev.target.value, ct);
                  }
                }}
              />
              {part &&
                textElements(
                  splitText(part),
                  style,
                  variant || 'elementText',
                  'span'
                )}
            </>
          );
        })}
      </Box>
    );
  };

  const renderElement = (element: ElementType) => {
    const elementType =
      element instanceof InstructionElementType
        ? element.instruction.case
        : element.model.case;
    const elementValue =
      element instanceof InstructionElementType
        ? element.instruction.value
        : element.model.value;
    const elem: { text?: string; style?: SxProps; variant?: any } = {};
    const BlankTextRegExp = /_{3,}/g;
    const tagName =
      elementType === 'teacherNote'
        ? 'NOTE'
        : elementType === 'ask' || elementType == 'askQuestion'
        ? 'QUESTION'
        : elementType === 'explain'
        ? 'EXPLANATION'
        : elementType?.toUpperCase();
    switch (elementType) {
      case 'text': {
        if (elementValue instanceof TextType) {
          elem.text = elementValue.text;
          elem.style = styles.leftText;
        }
      }
      /* falls through */
      case 'textCenter': {
        if (elementValue instanceof TextCenterType) {
          elem.text = elementValue.textCenter;
          elem.style = styles.centerText;
        }
      }
      /* falls through */
      case 'textLeft': {
        if (elementValue instanceof TextLeftType) {
          elem.text = elementValue.textLeft;
          elem.style = styles.leftText;
        }
      }
      /* falls through */
      case 'textRight': {
        if (elementValue instanceof TextRightType) {
          elem.text = elementValue.textRight;
          elem.style = styles.rightText;
        }
      }
      /* falls through */
      case 'textBox': {
        if (elementValue instanceof TextBoxType) {
          elem.text = elementValue.boxText;
          elem.style = {};
          elem.variant = 'elementText';
        }
      }
      /* falls through */
      case 'heading': {
        if (elementValue instanceof HeadingType) {
          elem.text = elementValue.heading;
          elem.style = styles.heading;
          elem.variant = 'elementH1';
        }
      }
      /* falls through */
      case 'subHeading': {
        if (elementValue instanceof SubHeadingType) {
          elem.text = elementValue.subHeading;
          elem.style = styles.subHeading;
          elem.variant = 'elementH2';
        }
        const { text, style, variant } = elem;
        const qParts = text?.split(BlankTextRegExp) || [];
        if (isFibText) {
          return renderFibText(qParts, style, variant);
        }
        return text ? (
          <Box
            sx={
              elementType == 'textBox'
                ? styles.textBoxWrapper
                : styles.textElementsWrapper
            }
          >
            {textElements(splitText(text), style, variant || 'elementText')}
          </Box>
        ) : null;
      }

      case 'teacherImage':
      /* falls through */
      case 'image':
        if (hideImage) {
          return null;
        }
        if (elementValue instanceof ImageType) {
          return (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box sx={styles.imageWrapper}>
                  <ImageWrapper
                    name={elementValue.imageName}
                    path={getMediaBasePath(
                      elementValue.imageUrl,
                      resourceCategory ===
                        ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                        ? 'userDataBucket'
                        : 'processedMediaBucket'
                    )}
                    type={'png'}
                    styles={
                      imageResizeEnabled ? styles.image : styles.imageNormal
                    }
                  />
                  <ImageComments
                    imageUrl={elementValue.imageUrl}
                    leading={
                      imageResizeEnabled ? (
                        <Box
                          sx={styles.resizeBox}
                          onClick={() => setOpenImageUrl(elementValue.imageUrl)}
                        >
                          <TypographyHtml
                            variant="subText"
                            sx={{ display: { xs: 'none', md: 'block' } }}
                          >
                            View Full Image
                          </TypographyHtml>
                          <ImageWrapper
                            name={'resizeBox'}
                            type={'png'}
                            parentFolder="icons"
                            styles={styles.resize}
                          />
                        </Box>
                      ) : null
                    }
                  />
                  {imageResizeEnabled && (
                    <ImagePopup
                      imageName={elementValue.imageName}
                      imageUrl={getMediaBasePath(
                        elementValue.imageUrl,
                        resourceCategory ===
                          ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                          ? 'userDataBucket'
                          : 'processedMediaBucket'
                      )}
                      open={openImageUrl === elementValue.imageUrl}
                      handleClose={() => setOpenImageUrl(null)}
                    />
                  )}
                </Box>
              </Box>
              {/* {elementValue.imageName && textElements(splitText(elementValue.imageName), {}, 'h1')} */}
              {elementValue.imageDescription &&
                textElements(
                  splitText(elementValue.imageDescription),
                  {},
                  'elementText'
                )}
            </>
          );
        }
        return null;
      case 'audio':
        if (elementValue instanceof AudioType) {
          return (
            <>
              <Box sx={styles.audioBox}>
                <ImageWrapper
                  name={'audio'}
                  type={'png'}
                  parentFolder="icons"
                  styles={styles.audioImage}
                />
                <Box sx={styles.audioWrapper}>
                  <AudioPlayer
                    autoPlay={false}
                    src={getMediaBasePath(
                      elementValue.audioUrl,
                      resourceCategory ===
                        ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                        ? 'userDataBucket'
                        : 'processedMediaBucket'
                    )}
                    // onPlay={e => console.log("onPlay")}
                    customControlsSection={[
                      RHAP_UI.CURRENT_TIME,
                      <div>/</div>,
                      RHAP_UI.DURATION,
                    ]}
                    customProgressBarSection={[
                      RHAP_UI.MAIN_CONTROLS,
                      RHAP_UI.PROGRESS_BAR,
                    ]}
                    showJumpControls={false}
                    // other props here
                  />
                </Box>
              </Box>
              {/* <Box component="audio" controls>
            <source src={elementValue.audioUrl} />
          </Box> */}
              {elementValue.audioName &&
                textElements(
                  splitText(elementValue.audioName),
                  {},
                  'elementH1'
                )}
              {elementValue.audioDescription &&
                textElements(
                  splitText(elementValue.audioDescription),
                  {},
                  'elementText'
                )}
            </>
          );
        }
        return null;
      case 'teacherVideo':
      /* falls through */
      case 'video':
        if (elementValue instanceof VideoType) {
          const {
            videoName,
            videoUrl,
            videoDescription,
            thumbnailImageUrl,
            credits,
            subTitles,
            externalSubtitlesSrtUrl,
          } = elementValue;
          // The element carries the default language; an override swaps in
          // the selected one. Both play through the same element and player.
          const resolvedVideoUrl = videoUrlOverride || videoUrl;
          const options: any = {};
          if (thumbnailImageUrl) {
            options.poster = getMediaBasePath(
              thumbnailImageUrl,
              resourceCategory ===
                ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                ? 'userDataBucket'
                : 'processedMediaBucket'
            );
          }
          return (
            <>
              {/* <VideoPlayer options={options} videoUrl={videoUrl} /> */}
              <VideoPlayerWrapper
                key={resolvedVideoUrl}
                options={options}
                videoUrl={resolvedVideoUrl}
                selected_resource_info={selected_resource_info}
                resourceCategory={resourceCategory}
                allowUserControls={allowUserControls}
                annotationsEnabled={annotationsEnabled}
              />

              {/* {videoName && textElements(splitText(videoName), {}, 'h1')} */}
              {videoDescription &&
                textElements(splitText(videoDescription), {}, 'elementText')}
            </>
          );
        }
        return null;
      case 'numberList':
        if (elementValue instanceof NumberedListType) {
          return (
            <Box component="ol" sx={styles.bulletList}>
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementText', 'li');
                }
                return (
                  <li key={index}>
                    {textElements(splitText(text), {}, 'elementText', 'span')}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'bulletList':
        if (elementValue instanceof BulletedListType) {
          return (
            <Box component="ul" sx={styles.bulletList}>
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementText', 'li');
                }
                return (
                  <li key={index}>
                    {textElements(splitText(text), {}, 'elementText', 'span')}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'askQuestion':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(splitText(elementValue), {}, 'elementText', 'span')}
            </>
          );
        }
        return null;
      case 'askAnswer':
        if (typeof elementValue == 'string') {
          return (
            <Accordion sx={styles.accordion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <TypographyHtml variant={'elementH3'}>Answer</TypographyHtml>
              </AccordionSummary>
              <AccordionDetails>
                {textElements(
                  splitText(elementValue),
                  {},
                  'elementText',
                  'div'
                )}
              </AccordionDetails>
            </Accordion>
          );
        }
        return null;
      case 'ask':
        if (elementValue instanceof AskInstructionModel) {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue.question),
                {},
                'elementText',
                'span'
              )}
              <Accordion sx={styles.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <TypographyHtml variant={'elementH3'}>Answer</TypographyHtml>
                </AccordionSummary>
                <AccordionDetails>
                  {textElements(
                    splitText(elementValue.answer),
                    {},
                    'elementText',
                    'div'
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          );
        }
        return null;
      case 'do':
      case 'teacherNote':
      case 'note':
      case 'explain':
      case 'say':
      case 'discuss':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(splitText(elementValue), {}, 'elementText', 'span')}
            </>
          );
        }
        return null;
      case 'comprehensionPassage':
        if (elementValue instanceof ComprehensionPassage) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.comprehensionPassageText),
                {},
                'elementText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      case 'table':
        if (elementValue instanceof TableType && elementValue.table) {
          return (
            <TableElement
              resourceCategory={resourceCategory}
              table={elementValue.table}
            />
          );
        }
        return null;
      case 'excerpt':
        if (elementValue instanceof ExcerptType) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.excerpt),
                {},
                'elementText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      case 'externalResource':
        if (elementValue instanceof ExternalResourceType) {
          return (
            <Box>
              <FilePreviewCard
                element={{
                  disableDownload: disableDownload,
                  fileExtensionType: elementValue.fileExtensionType,
                  downloadUrl: getMediaBasePath(
                    elementValue.fileUrl,
                    resourceCategory ===
                      ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                      ? 'userDataBucket'
                      : 'processedMediaBucket'
                  ),
                  fileSizeInMb: elementValue.fileSizeInMb,
                  fileName: elementValue.fileName,
                  fileType: elementValue.fileType,
                  filePreviewUrl: getMediaBasePath(
                    elementValue.previewUrl,
                    resourceCategory ===
                      ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                      ? 'userDataBucket'
                      : 'processedMediaBucket'
                  ),
                  gcpFileUrl: getMediaBasePath(
                    elementValue.fileUrl,
                    resourceCategory ===
                      ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                      ? 'userDataBucket'
                      : 'processedMediaBucket'
                  ),
                  openPdfInNewTab,
                }}
              />
              {/* <ExternalResource element={elementValue} /> */}
            </Box>
          );
        }
        return null;
      default:
        return <Box sx={{ color: 'red' }}>Invalid Element</Box>;
    }
  };

  return (
    <Box
      sx={{
        gap: {
          xs: pxToRem(15),
          md: pxTovW(20),
        },
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflowX: {
          xs: 'auto',
          md: 'visible',
        },
        overflowY: {
          xs: 'auto',
          md: 'visible',
        },
      }}
    >
      {elements.map((element, i) => {
        return (
          <React.Fragment key={i}>{renderElement(element)}</React.Fragment>
        );
      })}
    </Box>
  );
};

export default ElementRenderer;
